import type { EditorExpressionFormValues } from "./schema-builder-field-editor-expression.type";
import type { GetEditorValuesOptions } from "../schema-builder-field-editor.type";
import type { SchemaFieldExpression } from "@/entities/schema";

import isEqual from "lodash/isEqual";

import { isEqualNumber, isValidNumber } from "@/utilities/number";
import { isEqualString } from "@/utilities/string";

import {
  getExpressionValuesFromSchema,
  getSchemaExpressionNodeFromValues,
} from "../../schema-builder-expression";
import { isNewSchemaField } from "../../schema-builder.helper";
import {
  getEditorDefaultValues,
  getNewSchemaFieldOptionsFromValues,
  isEditorDefaultFormValuesEqual,
} from "../schema-builder-field-editor-default";

export const getEditorExpressionValues = (
  options: GetEditorValuesOptions
): EditorExpressionFormValues => {
  const { schema } = options;

  const values: EditorExpressionFormValues = {
    fieldType: "expression",
    expression: { formula: "", variables: [] },
    ...getEditorDefaultValues(options),
  };

  if (isNewSchemaField(schema)) values.precision = 2;

  if (schema.type !== "expression") return values;

  values.expression = getExpressionValuesFromSchema(schema);
  if (schema.description) values.description = schema.description;
  if (isValidNumber(schema.precision)) values.precision = schema.precision;
  if (schema.unit) values.unit = schema.unit;

  return values;
};

export const isEditorExpressionFormValuesEqual = (
  a: EditorExpressionFormValues,
  b: EditorExpressionFormValues
) => {
  if (!isEditorDefaultFormValuesEqual(a, b)) return false;
  if (!isEqual(a.expression, b.expression)) return false;
  if (!isEqualString(a.description, b.description)) return false;
  if (!isEqualNumber(a.precision, b.precision)) return false;
  if (!isEqualString(a.unit, b.unit)) return false;
  return true;
};

export const getNewSchemaExpressionFromValues = (
  values: EditorExpressionFormValues
): SchemaFieldExpression | null => {
  const nodeAst = getSchemaExpressionNodeFromValues(values.expression);

  if (!nodeAst) return null;

  const schema: SchemaFieldExpression = {
    type: "expression",
    expression: nodeAst,
    ...getNewSchemaFieldOptionsFromValues(values),
  };

  if (values.description) schema.description = values.description;
  if (isValidNumber(values.precision)) schema.precision = values.precision;
  if (values.unit) schema.unit = values.unit;

  return schema;
};
